import { AnimatePresence, motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
  Check,
  XCircle,
  DollarSign,
  Tag,
  Gauge,
  Download,
  Trash2,
  X,
} from "lucide-react";
import { Button } from "@cieda/djayma-ui";
import type { Product } from "@/lib/validation/product";

interface BulkActionBarProps {
  readonly selectedIds: ReadonlySet<string>;
  readonly selectedProducts: readonly Product[];
  readonly totalFiltered: number;
  readonly isAllFilteredSelected: boolean;
  readonly onSelectAllFiltered: () => void;
  readonly onClearSelection: () => void;
  readonly onActivate: () => void;
  readonly onDeactivate: () => void;
  readonly onPrice: () => void;
  readonly onCategory: () => void;
  readonly onThreshold: () => void;
  readonly onExportCsv: () => void;
  readonly onDelete: () => void;
  readonly isLoading: boolean;
}

/**
 * Sticky bottom bulk action bar. Slides up from the bottom when at least one
 * product is selected. Honors prefers-reduced-motion via framer-motion defaults.
 */
export function BulkActionBar({
  selectedIds,
  selectedProducts,
  totalFiltered,
  isAllFilteredSelected,
  onSelectAllFiltered,
  onClearSelection,
  onActivate,
  onDeactivate,
  onPrice,
  onCategory,
  onThreshold,
  onExportCsv,
  onDelete,
  isLoading,
}: BulkActionBarProps) {
  const { t } = useTranslation("admin");
  const count = selectedIds.size;

  return (
    <AnimatePresence>
      {count > 0 && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
          className="fixed bottom-0 inset-x-0 z-30 border-t border-stone-200 bg-white/95 backdrop-blur-sm px-4 py-3 shadow-lg sm:px-6"
          role="region"
          aria-label={t("products.bulk.selected", { count })}
        >
          <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-3">
            {/* Left: count + select-all banner */}
            <div className="flex items-center gap-3 text-sm">
              <span className="font-medium text-stone-900">
                {t("products.bulk.selected", { count })}
              </span>
              {!isAllFilteredSelected && count === selectedProducts.length && totalFiltered > count && (
                <button
                  type="button"
                  className="text-amber-700 underline underline-offset-2 hover:text-amber-900"
                  onClick={onSelectAllFiltered}
                >
                  {t("products.bulk.select_all_filtered", {
                    count: totalFiltered,
                  })}
                </button>
              )}
              <button
                type="button"
                className="text-stone-500 hover:text-stone-700"
                onClick={onClearSelection}
                aria-label={t("products.bulk.deselect")}
              >
                <X className="h-4 w-4" />
                <span className="sr-only">
                  {t("products.bulk.deselect")}
                </span>
              </button>
            </div>

            {/* Right: action buttons */}
            <div className="flex items-center gap-1.5">
              <Button
                variant="outline"
                size="sm"
                onClick={onActivate}
                disabled={isLoading}
                className="gap-1"
              >
                <Check className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">
                  {t("products.bulk.activate")}
                </span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={onDeactivate}
                disabled={isLoading}
                className="gap-1"
              >
                <XCircle className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">
                  {t("products.bulk.deactivate")}
                </span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={onPrice}
                disabled={isLoading}
                className="gap-1"
              >
                <DollarSign className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">
                  {t("products.bulk.price")}
                </span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={onCategory}
                disabled={isLoading}
                className="gap-1"
              >
                <Tag className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">
                  {t("products.bulk.category")}
                </span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={onThreshold}
                disabled={isLoading}
                className="gap-1"
              >
                <Gauge className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">
                  {t("products.bulk.threshold")}
                </span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={onExportCsv}
                disabled={isLoading}
                className="gap-1"
              >
                <Download className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">
                  {t("products.bulk.export_csv")}
                </span>
              </Button>

              {/* Destructive -- visually separated */}
              <div className="ml-2 border-l border-stone-200 pl-2">
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={onDelete}
                  disabled={isLoading}
                  className="gap-1"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">
                    {t("products.bulk.delete")}
                  </span>
                </Button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
